import type { ReadinessCriterion, ReadinessLevelResult } from "@/lib/api/work-items";
import { cn } from "@/lib/utils";

/**
 * "Before the team can size this" (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1401): every grooming rule of the item's kind, each marked in
 * place or missing with a plain explanation, under "N of M in place".
 *
 * E280 decision 5: the rubric wins over the design's sample "N of 5": bugs
 * have 4 grooming rules and stories 2. The rows come from
 * `readiness.grooming.criteria`; readiness stored before E280 has no
 * criteria, so the list falls back to the missing rules only.
 */

/** The rows to show: every criterion, or the missing rules when the read has no criteria. */
export function checklistRows(readiness: Pick<ReadinessLevelResult, "missing" | "criteria">): ReadinessCriterion[] {
  if (readiness.criteria && readiness.criteria.length > 0) return readiness.criteria;
  return readiness.missing.map((m) => ({
    id: m.id,
    label: m.label,
    severity: m.severity,
    present: false,
    explanation: m.missingMessage,
    missingMessage: m.missingMessage,
  }));
}

/** "3 of 4 in place", or "2 missing" when only the missing rules are known. */
export function inPlaceText(readiness: Pick<ReadinessLevelResult, "missing" | "criteria">): string {
  const rows = checklistRows(readiness);
  if (!readiness.criteria || readiness.criteria.length === 0) return `${String(rows.length)} missing`;
  const present = rows.filter((row) => row.present).length;
  return `${String(present)} of ${String(rows.length)} in place`;
}

export function ReadinessChecklist({ readiness }: { readiness: Pick<ReadinessLevelResult, "missing" | "criteria"> }) {
  const rows = checklistRows(readiness);
  return (
    <section className="glass-card rounded-[10px]" data-testid="readiness-checklist" aria-label="Before the team can size this">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <h2 className="flex-1 font-display text-sm font-semibold">Before the team can size this</h2>
        <span className="text-xs text-muted-foreground" data-testid="in-place-count">
          {inPlaceText(readiness)}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-2.5 text-sm text-muted-foreground">The readiness check did not say what is missing.</p>
      ) : (
        <ul>
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-start gap-2.5 border-b border-[#1A1A1E] px-3 py-2.5 last:border-b-0"
              data-testid="readiness-criterion"
              data-present={row.present ? "true" : "false"}
            >
              <span
                className={cn(
                  "flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[10px] font-bold",
                  row.present ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"
                )}
                aria-label={row.present ? "In place" : "Missing"}
                role="img"
              >
                {row.present ? "✓" : "!"}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-medium">
                  {row.label}
                  {row.severity !== "critical" && <span className="font-normal text-muted-foreground"> (nice to have)</span>}
                </span>
                <span className="text-[13px] leading-relaxed text-muted-foreground">{row.explanation}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
